import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/categories';

type ArticleData = {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  cover: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  views: string;
  tags: string[];
  content: string;
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<ArticleData | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<ArticleData[]>([]);

  useEffect(() => {
    const articleId = parseInt(id || '1');
    const found = articles.find((a) => a.id === articleId);
    if (found) {
      setArticle(found);
      document.title = `${found.title} - 快猫短视频`;
      const related = articles.filter((a) => a.id !== found.id && a.category === found.category).slice(0, 3);
      if (related.length < 3) {
        const others = articles.filter((a) => a.id !== found.id && a.category !== found.category).slice(0, 3 - related.length);
        setRelatedArticles([...related, ...others]);
      } else {
        setRelatedArticles(related);
      }
    } else {
      navigate('/');
    }
  }, [id, navigate]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin"></i>
          <p className="mt-4 text-foreground-500">加载中...</p>
        </div>
      </div>
    );
  }

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.cover,
    url: `https://www.kuaimao.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '快猫短视频',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '资讯', to: '/articles' },
              { label: article.title },
            ]}
          />

          <article className="w-full py-8 md:py-12">
            <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  {article.category}
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    阅读约{article.readTime}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views}次阅读
                  </span>
                </div>
              </div>

              <div className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={article.cover}
                  alt={article.title}
                  className="w-full aspect-[16/9] object-cover"
                />
              </div>

              <div
                className="prose max-w-none text-foreground-700 leading-relaxed mb-10 article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mb-10 pt-6 border-t border-background-200">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {relatedArticles.length > 0 && (
                <div className="border-t border-background-200 pt-10">
                  <h2 className="text-lg font-bold font-heading text-foreground-950 mb-6">
                    相关文章
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.id}`}
                        className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                      >
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={ra.cover}
                            alt={ra.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                            {ra.title}
                          </h3>
                          <div className="flex items-center gap-3 text-xs text-foreground-400">
                            <span>{ra.date}</span>
                            <span>{ra.views}次阅读</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}